// Story 4.4 — visual prototype for <OriginLinkFooter />.
//
// Renders 4 examples on a paper-100 background so visual review can
// confirm the golden traceback line animation + the inline vs prominent
// size variants + the 40-char truncation behavior. Hand-rolled
// CertificateShare fixtures (no API call; pure presentation).

import type { FC } from "react";
import { OriginLinkFooter, type CertificateShare } from "@greenhold/components";

function makeShare(overrides?: Partial<CertificateShare>): CertificateShare {
  return {
    share_id: "00000000-0000-0000-0000-000000000001",
    dossier_id: "00000000-0000-0000-0000-000000000002",
    drop_id: "00000000-0000-0000-0000-000000000003",
    portfolio_id: "00000000-0000-0000-0000-000000000004",
    portfolio_title: "Spring Champions 2026",
    tier: "common",
    edition_number: 42,
    share_serial: 7,
    creator_id: "00000000-0000-0000-0000-000000000005",
    creator_handle: "caps",
    snapshot_display_name: "Caps",
    snapshot_hello_text: "GG.",
    snapshot_image_url: "creator-images/x.jpg",
    snapshot_bio: null,
    snapshot_image_signed_url: null,
    snapshot_image_signed_url_expires_at: null,
    issued_at: "2026-05-25T09:00:00Z",
    opened_at: "2026-05-25T09:00:00Z",
    is_highlighted_by_owner: false,
    is_favourited_by_owner: false,
    condition: 0,
    signed: false,
    ...overrides,
  };
}

const shortShare = makeShare();
const longShare = makeShare({
  portfolio_title:
    "A very long portfolio title that exceeds the forty-character truncation limit",
});

export const OriginLinkFooterPrototype: FC = () => {
  return (
    <main className="min-h-screen bg-brand-paper-100 p-8 font-body">
      <h1 className="font-heading text-2xl font-bold mb-6">
        OriginLinkFooter — Story 4.4 visual review
      </h1>
      <p className="text-sm opacity-70 mb-8">
        Hover each example to see the golden traceback line animate
        left-to-right (0.4s, per UX-spec L1599). Click navigates to{" "}
        <code>/shop/&#123;drop_id&#125;</code>.
      </p>

      <section className="space-y-8">
        <Example
          title="Inline variant (default) — short title"
          description="10px monospace gold. Used inline with every <Certificate>."
        >
          <OriginLinkFooter share={shortShare} variant="inline" />
        </Example>

        <Example
          title="Inline variant — long title (truncates at 40 chars)"
          description="Long portfolio_title falls back to ellipsis after 40 chars; aria-label retains the full untruncated title for screen readers."
        >
          <OriginLinkFooter share={longShare} variant="inline" />
        </Example>

        <Example
          title="Prominent variant — share-detail page footer"
          description="12px monospace gold. Used on the share-detail hero footer."
        >
          <OriginLinkFooter share={shortShare} variant="prominent" />
        </Example>

        <Example
          title="Side-by-side comparison"
          description="Inline vs prominent at the same line height for visual size diff."
        >
          <div className="flex items-baseline gap-6">
            <OriginLinkFooter share={shortShare} variant="inline" />
            <OriginLinkFooter share={shortShare} variant="prominent" />
          </div>
        </Example>
      </section>
    </main>
  );
};

interface ExampleProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function Example({ title, description, children }: ExampleProps) {
  return (
    <article className="rounded-lg border border-border bg-brand-paper-50 p-6 space-y-3">
      <header className="space-y-1">
        <h2 className="font-heading text-base font-semibold">{title}</h2>
        <p className="text-xs opacity-70">{description}</p>
      </header>
      <div className="flex items-center justify-center min-h-12 py-4">
        {children}
      </div>
    </article>
  );
}
